'use client'

/**
 * MultiSelectDropdown - a searchable multi-select rendered as a popover
 * (trigger button + dropdown with a search box, an optional "Select all" row,
 * and a scrollable checkbox list). The platform's answer to long option lists
 * (e.g. a 150+ geo-zone attribute): instead of an inline wall of checkboxes,
 * the field collapses to a compact trigger that summarises the selection and
 * opens a searchable checklist on demand.
 *
 * Mirrors SearchableSelect's Popover/search styling so the two feel like one
 * family; the difference is multi-value (checkbox rows, no close-on-select)
 * plus an opt-in select-all.
 *
 * What is chosen is always visible: as removable chips under the trigger, and
 * in a "Selected" strip at the top of the open list, so the list never hides
 * the user's picks.
 */

import * as React from 'react'
import { Popover } from '@base-ui/react/popover'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Search, X } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'

/** Chips shown under the trigger before collapsing the rest into "+N more". */
const VISIBLE_CHIPS = 8

export interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectDropdownProps {
  value: string[]
  onChange: (value: string[]) => void
  options: MultiSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  /** Show a "Select all" row that toggles every option. */
  allowSelectAll?: boolean
  disabled?: boolean
  triggerClassName?: string
  'data-testid'?: string
}

export function MultiSelectDropdown({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  allowSelectAll = false,
  disabled = false,
  triggerClassName,
  'data-testid': testId,
}: MultiSelectDropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const searchRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }))
    } else {
      setSearch('')
    }
  }, [open])

  const selected = React.useMemo(() => new Set(value), [value])

  const labelByValue = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const o of options) map.set(o.value, o.label)
    return map
  }, [options])

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, search])

  const allValues = React.useMemo(() => options.map((o) => o.value), [options])
  const allSelected = allValues.length > 0 && allValues.every((v) => selected.has(v))

  const toggle = (optionValue: string) => {
    const next = selected.has(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue]
    onChange(next)
  }

  // Trigger summary: the placeholder, or how many are chosen - the chosen items
  // themselves show as chips under it.
  const summary =
    value.length === 0
      ? placeholder
      : t('multiSelect.countSelected', { defaultValue: '{{count}} selected', count: value.length })

  const remove = (optionValue: string) => onChange(value.filter((v) => v !== optionValue))

  const chip = (optionValue: string) => {
    const label = labelByValue.get(optionValue) ?? optionValue
    return (
      <Badge
        key={optionValue}
        variant="secondary"
        className="max-w-[240px] shrink-0 gap-1 pr-0.5 font-normal"
        title={label}
      >
        <span className="min-w-0 truncate">{label}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t('multiSelect.remove', { defaultValue: 'Remove {{label}}', label })}
          onClick={() => remove(optionValue)}
          disabled={disabled}
          className="size-4 shrink-0 rounded-full"
        >
          <X className="size-3" />
        </Button>
      </Badge>
    )
  }

  return (
    <div className="flex flex-col gap-2">
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        data-slot="multi-select-trigger"
        data-testid={testId}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between gap-1.5 rounded-[8px] border border-input bg-transparent px-3 py-2 text-left text-[13px] transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
          triggerClassName,
        )}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            value.length === 0 && 'text-[var(--ink-3)]',
          )}
        >
          {summary}
        </span>
        <ChevronDown className="pointer-events-none size-4 shrink-0 text-[var(--ink-3)]" />
      </Popover.Trigger>

      <Popover.Portal>
        {/* Always open below the box, never flipped up over the fields above
            it (and the choices being made); the list shrinks to fit instead. */}
        <Popover.Positioner
          side="bottom"
          sideOffset={4}
          align="start"
          collisionAvoidance={{ side: 'none', align: 'shift', fallbackAxisSide: 'none' }}
          collisionPadding={12}
          className="isolate z-50"
        >
          <Popover.Popup
            data-slot="multi-select-content"
            onKeyDown={(event) => event.stopPropagation()}
            className="flex max-h-(--available-height) w-(--anchor-width) min-w-[240px] flex-col origin-(--transform-origin) rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)] duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
          >
            <div className="border-b-[0.5px] border-[var(--rule)] p-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-7 w-full rounded-md bg-[var(--bg-subtle)] pl-7 pr-2 text-[12px] text-[var(--ink-1)] outline-none placeholder:text-[var(--ink-3)]"
                />
              </div>
            </div>

            {value.length > 0 && (
              <div className="border-b-[0.5px] border-[var(--rule)] px-2 py-1.5">
                <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-[var(--ink-3)]">
                  <span>
                    {t('multiSelect.selectedHeading', {
                      defaultValue: 'Selected ({{count}})',
                      count: value.length,
                    })}
                  </span>
                  <Button
                    type="button"
                    variant="link"
                    size="xs"
                    onClick={() => onChange([])}
                    className="h-auto px-0 text-[11px]"
                  >
                    {t('multiSelect.clear', 'Clear')}
                  </Button>
                </div>
                {/* One compact, scrollable row, so the list below keeps its room. */}
                <div className="flex gap-1 overflow-x-auto pb-0.5">{value.map(chip)}</div>
              </div>
            )}

            <div className="max-h-[240px] min-h-[120px] flex-1 overflow-y-auto p-1">
              {allowSelectAll && allValues.length > 0 && (
                <Label className="flex cursor-pointer items-center gap-2.5 rounded-md border-b-[0.5px] border-b-[var(--rule)] px-2 py-1.5 font-normal">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={() => onChange(allSelected ? [] : allValues)}
                  />
                  <span className="text-[13px] font-medium text-[var(--ink-2)]">
                    Select all
                  </span>
                </Label>
              )}
              {filtered.length === 0 ? (
                <div className="px-2 py-4 text-center text-[12px] text-[var(--ink-3)]">
                  {emptyMessage}
                </div>
              ) : (
                filtered.map((option) => (
                  <Label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 font-normal hover:bg-[var(--bg-subtle)]"
                  >
                    <Checkbox
                      checked={selected.has(option.value)}
                      onCheckedChange={() => toggle(option.value)}
                    />
                    <span className="text-[13px] text-[var(--ink-2)]">
                      {option.label}
                    </span>
                  </Label>
                ))
              )}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
    {value.length > 0 && !open && (
      <div className="flex flex-wrap gap-1.5" data-slot="multi-select-chips">
        {value.slice(0, VISIBLE_CHIPS).map(chip)}
        {value.length > VISIBLE_CHIPS && (
          <Button
            type="button"
            variant="link"
            size="xs"
            onClick={() => setOpen(true)}
            className="h-auto px-0 text-[12px]"
          >
            {t('multiSelect.more', {
              defaultValue: '+{{count}} more',
              count: value.length - VISIBLE_CHIPS,
            })}
          </Button>
        )}
      </div>
    )}
    </div>
  )
}
